import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ShieldCheck } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import TwoFactorEnrollDialog from './TwoFactorEnrollDialog';
import { getTwoFactorStatus } from '@/services/twoFactorService';
import { useLanguage } from '@/contexts/LanguageContext';

const PROMPT_FLAG = '2fa-prompt-pending';
const DISMISS_FLAG = '2fa-prompt-dismissed';

/** Proposition d'activer la 2FA juste après une connexion. */
const TwoFactorPrompt = () => {
  const { user, token } = useAuth();
  const [open, setOpen] = useState(false);
  const [enrollOpen, setEnrollOpen] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    if (!user || !token) return;
    if (localStorage.getItem(PROMPT_FLAG) !== '1') return;
    if (localStorage.getItem(DISMISS_FLAG) === '1') return;

    let cancelled = false;
    (async () => {
      try {
        const s = await getTwoFactorStatus();
        if (!cancelled && !s.enabled) setOpen(true);
        else localStorage.removeItem(PROMPT_FLAG);
      } catch {
        /* silencieux */
      }
    })();
    return () => { cancelled = true; };
  }, [user, token]);

  const close = (remember: boolean) => {
    localStorage.removeItem(PROMPT_FLAG);
    if (remember) localStorage.setItem(DISMISS_FLAG, '1');
    setOpen(false);
  };

  if (!user) return null;

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => { if (!v) close(false); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="mx-auto h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center mb-2">
              <ShieldCheck className="h-7 w-7 text-primary" />
            </div>
            <DialogTitle className="text-center text-xl">{t('login.secureYourAccount')}</DialogTitle>
            <DialogDescription className="text-center">
              {t('login.secureYouAccountDesc')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col sm:flex-col gap-2">
            <Button className="w-full h-11" onClick={() => { setOpen(false); setEnrollOpen(true); }}>
              {t('login.enableNow')}
            </Button>
            <Button variant="ghost" className="w-full" onClick={() => close(false)}>
              {t('login.nextTime')}
            </Button>
            <button
              type="button"
              className="text-xs text-muted-foreground hover:text-foreground mx-auto"
              onClick={() => close(true)}
            >
              {t('login.noMorePrompt')}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <TwoFactorEnrollDialog
        open={enrollOpen}
        onOpenChange={(v) => { setEnrollOpen(v); if (!v) localStorage.removeItem(PROMPT_FLAG); }}
        onEnrolled={() => localStorage.removeItem(PROMPT_FLAG)}
      />
    </>
  );
};

export default TwoFactorPrompt;
